import React, { useEffect, useMemo, useState } from 'react';
import { Alert } from '@mui/material';
import { businessLeadService } from '../../services/api/businessLeadService';
import { EMPTY_MATCH_BUSINESS_ID } from '../../utils/emptyMatchWaitlist';
import { getInquiryDisplayNames } from '../../utils/serviceTypeDisplay';
import { mapBusinessLeadToWizardState } from '../../utils/wizardSearchRequestLink';
import { wizardServiceRegistry } from '../../constants/wizardServiceRegistry';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import WizardRequestSummaryDialog from '../common/WizardRequestSummaryDialog/WizardRequestSummaryDialog';
import AdminWaitlistAssignModal from './AdminWaitlistAssignModal';
import {
  AssignButton,
  EmptyState,
  HotspotArea,
  HotspotCell,
  HotspotHeadCell,
  HotspotRow,
  HotspotTable,
  HotspotTableWrap,
  HotspotTrade,
  LeadBody,
  LeadCard,
  LeadChip,
  LeadChipRow,
  LeadContact,
  LeadDetails,
  LeadHeader,
  LeadList,
  LeadTitle,
  LeadWhen,
  SectionLabel,
  SectionLabelSpaced,
  SectionPanel,
  SignupCount,
  StatCard,
  StatLabel,
  StatRow,
  StatValue,
  WaitlistIntro,
} from './AdminWaitlist.styled';

function categoryLabel(category) {
  if (category === 'hvac') return 'HVAC';
  return wizardServiceRegistry[category]?.serviceLabel || category;
}

function formatWhen(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso || '';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function formatPhone(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length !== 10) return value || '';
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function contactLine(lead) {
  const requester = lead.requester || {};
  if (requester.preferredContactMethod === 'phone') {
    return `Phone · ${formatPhone(requester.phone)}`;
  }
  if (requester.preferredContactMethod === 'text') {
    return `Text · ${formatPhone(requester.phone)}`;
  }
  if (requester.preferredContactMethod === 'email') {
    return `Email · ${requester.email || ''}`;
  }
  return requester.email || formatPhone(requester.phone) || '';
}

function leadName(lead) {
  const requester = lead.requester || {};
  return [requester.firstName, requester.lastName].filter(Boolean).join(' ') || 'Homeowner';
}

function summarizeLeads(leads) {
  const groups = new Map();
  leads.forEach((lead) => {
    const area = [lead.city, lead.state].filter(Boolean).join(', ');
    const key = `${lead.serviceType}|${area}`;
    const group = groups.get(key) || {
      category: lead.serviceType,
      area,
      submissions: 0,
    };
    group.submissions += 1;
    groups.set(key, group);
  });
  const hotspots = [...groups.values()].sort((a, b) => {
    if (b.submissions !== a.submissions) return b.submissions - a.submissions;
    return String(a.category).localeCompare(String(b.category)) || String(a.area).localeCompare(String(b.area));
  });
  const recent = [...leads].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
  return { submissions: leads.length, hotspots, leads: recent };
}

function AdminWaitlist({
  businesses = [],
  businessesLoading = false,
  businessesError = null,
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [assignLead, setAssignLead] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [summaryLead, setSummaryLead] = useState(null);

  const summaryWizardState = useMemo(
    () => (summaryLead ? mapBusinessLeadToWizardState(summaryLead) : null),
    [summaryLead],
  );
  const summaryDisplayNames = useMemo(() => {
    if (!summaryLead) return { tradeName: '', subserviceName: '' };
    return getInquiryDisplayNames(
      summaryLead.serviceType,
      summaryLead.subServiceType,
      summaryLead.targetServiceTitle,
      summaryLead.inquiryKind,
    );
  }, [summaryLead]);

  useEffect(() => {
    let cancelled = false;
    businessLeadService
      .listByBusiness(EMPTY_MATCH_BUSINESS_ID)
      .then((leads) => {
        if (!cancelled) setData(summarizeLeads(Array.isArray(leads) ? leads : []));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Could not load waitlist leads.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAssign = async (businessId) => {
    if (!assignLead) return;
    setAssigning(true);
    try {
      await businessLeadService.assignToBusiness(assignLead.id, businessId);
      setData((prev) => {
        const remaining = (prev?.leads || []).filter((row) => row.id !== assignLead.id);
        return summarizeLeads(remaining);
      });
      setAssignLead(null);
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return <NkySpinner label="Loading waitlist" />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {error}
      </Alert>
    );
  }

  const hotspots = data?.hotspots || [];
  const leads = data?.leads || [];

  return (
    <div>
      <WaitlistIntro>
        Homeowners who finished a search and found no pros. Assign a business when you
        have a match, or use the demand table to plan recruiting by city.
      </WaitlistIntro>
      <StatRow>
        <StatCard>
          <StatValue>{data?.submissions ?? 0}</StatValue>
          <StatLabel>Waitlist signups</StatLabel>
        </StatCard>
      </StatRow>

      <SectionLabel>Recent signups</SectionLabel>
      <SectionPanel>
        {leads.length === 0 ? (
          <EmptyState>No waitlist signups yet.</EmptyState>
        ) : (
          <LeadList>
            {leads.map((lead) => (
              <LeadCard key={lead.id}>
                <LeadBody
                  role="button"
                  tabIndex={0}
                  aria-label={`View request details for ${leadName(lead)}`}
                  onClick={() => setSummaryLead(lead)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSummaryLead(lead);
                    }
                  }}
                >
                  <LeadHeader>
                    <LeadTitle>{leadName(lead)}</LeadTitle>
                    <LeadWhen>{formatWhen(lead.createdAt)}</LeadWhen>
                  </LeadHeader>
                  <LeadChipRow>
                    <LeadChip>{categoryLabel(lead.serviceType)}</LeadChip>
                    {lead.city ? <LeadChip>{lead.city}</LeadChip> : null}
                  </LeadChipRow>
                  <LeadContact>{contactLine(lead)}</LeadContact>
                  {lead.details ? <LeadDetails>{lead.details}</LeadDetails> : null}
                </LeadBody>
                <AssignButton
                  type="button"
                  variant="contained"
                  disableElevation
                  disabled={assigning && assignLead?.id === lead.id}
                  onClick={() => setAssignLead(lead)}
                >
                  Assign
                </AssignButton>
              </LeadCard>
            ))}
          </LeadList>
        )}
      </SectionPanel>

      <SectionLabelSpaced>Demand by trade and city</SectionLabelSpaced>
      {hotspots.length === 0 ? (
        <SectionPanel>
          <EmptyState>No empty-match demand yet.</EmptyState>
        </SectionPanel>
      ) : (
        <HotspotTableWrap>
          <HotspotTable>
            <thead>
              <tr>
                {['Trade', 'City', 'Signups'].map((heading) => (
                  <HotspotHeadCell key={heading}>{heading}</HotspotHeadCell>
                ))}
              </tr>
            </thead>
            <tbody>
              {hotspots.map((row) => (
                <HotspotRow key={`${row.category}-${row.area}`}>
                  <HotspotCell>
                    <HotspotTrade>{categoryLabel(row.category)}</HotspotTrade>
                  </HotspotCell>
                  <HotspotCell>
                    <HotspotArea>{row.area || '—'}</HotspotArea>
                  </HotspotCell>
                  <HotspotCell>
                    <SignupCount>{row.submissions}</SignupCount>
                  </HotspotCell>
                </HotspotRow>
              ))}
            </tbody>
          </HotspotTable>
        </HotspotTableWrap>
      )}

      <AdminWaitlistAssignModal
        open={Boolean(assignLead)}
        lead={assignLead}
        businesses={businesses}
        businessesLoading={businessesLoading}
        businessesError={businessesError}
        saving={assigning}
        onClose={() => {
          if (!assigning) setAssignLead(null);
        }}
        onAssign={handleAssign}
      />

      <WizardRequestSummaryDialog
        open={Boolean(summaryLead)}
        onClose={() => setSummaryLead(null)}
        wizardState={summaryWizardState}
        serviceLabel={summaryDisplayNames.tradeName}
        subserviceLabel={summaryDisplayNames.subserviceName}
        urgencyLabel={
          summaryLead?.requestSummary?.urgency || summaryLead?.urgency || ''
        }
        helpRequested
      />
    </div>
  );
}

export default AdminWaitlist;
